import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { CodeEditor } from '@/components/webhooks/CodeEditor';

/** Parse JSON text; returns undefined for empty/whitespace, throws on bad JSON. */
export function tryParseJson(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  return JSON.parse(trimmed);
}

interface JsonFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Fired on every keystroke with the parsed value (or undefined when invalid/empty). */
  onParsed?: (value: Record<string, unknown> | undefined) => void;
  height?: number;
  hint?: string;
  /** Only require a JSON *object* (not any JSON value) — used for schema payloads. */
  requireObject?: boolean;
}

/**
 * Monaco JSON editor with live syntax validation. Mirrors the webhooks
 * CodeEditor pattern — the parent keeps the raw string, this component
 * surfaces parse errors inline and reports the parsed object upward.
 */
export function JsonField({
  label,
  value,
  onChange,
  onParsed,
  height = 160,
  hint,
  requireObject = false,
}: JsonFieldProps) {
  const [error, setError] = useState<string | null>(null);

  const handleChange = (next: string) => {
    onChange(next);
    try {
      const parsed = tryParseJson(next);
      if (requireObject && parsed !== undefined && (typeof parsed !== 'object' || Array.isArray(parsed))) {
        throw new Error('Expected a JSON object');
      }
      setError(null);
      onParsed?.(
        parsed !== undefined ? (parsed as Record<string, unknown>) : undefined,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON');
      onParsed?.(undefined);
    }
  };

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="overflow-hidden rounded-md border">
        <CodeEditor language="json" value={value} onChange={handleChange} height={height} />
      </div>
      {error ? (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="h-3 w-3 shrink-0" /> {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}